import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import type { BrowseCategoryRawFacet } from '@/utils/browsePath/browsePath';

import { SearchResultsAdContainer } from '@/components/Ads/SearchResultsAd/SearchResultsAdContainer';
import CompleteAccountBanner from '@/components/CompleteAccountBanner/CompleteAccountBanner';
import Pagination from '@/components/Pagination/hammer-pagination/Pagination';
import { PAGE_ITEMS_TO_VIEW } from '@/components/Pagination/helpers/filterSortOptions';
import { RelatedSearches } from '@/components/RelatedSearches/RelatedSearches';
import SearchError from '@/components/SearchError/SearchError';
import { useOptimizedSearchSelectors } from '@/hooks/useOptimizedSearchSelectors';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { changePaginationPageSize, getSearchPageSize } from '@/redux/modules/pagination';
import { getSaveSearchParams } from '@/redux/modules/saveSearch';
import { getSEOSearchTermData } from '@/redux/modules/search/search.selectors';
import paramTypes from '@/types/search/enums/paramTypes';
import { FilterAction } from '@/types/SearchFacet';
import { getStateName } from '@/utils/address';
import { TrackPageBottomReachedTrigger } from '@/utils/amplitude/analytics/pageBottomReached/TrackPageBottomReachedTrigger';
import { useLearningToRankSearchReRanking } from '@/utils/amplitude/experiments/hooks/useLearningToRankSearchReRanking';
import { usePopularityScoreReRanking } from '@/utils/amplitude/experiments/hooks/usePopularityScoreReRanking';
import cleanSearchTerms from '@/utils/cleanSearchTerms';
import { formatLocationString } from '@/utils/formatLocationFromSlug';
import { isEmpty } from '@/utils/isEmpty/isEmpty';
import { modifyParamsWithPage, modifyParamsWithPageSize, parseQueryParams } from '@/utils/queryParams';
import { buildSearchQuery } from '@/utils/search/searchQuery';
import { resolveSearchSeoPathSubjectToBrowseFacetIds } from '@/utils/search/searchSeoPath';
import {
    getIsCollectionsPage,
    getIsSearchPage,
    getIsSEOSearchPage,
    getIsSEOSearchPageWithLocation,
} from '@/utils/urls';

import { MarketingEventBanner } from './components/MarketingEventBanner/MarketingEventBanner';
import { RelatedSearchesSuggestions } from './components/RelatedSearchesSuggestions/RelatedSearchesSuggestions';
import { SearchPageHeader } from './components/SearchPageHeader/SearchPageHeader';
import { SearchPageHeaderMetaWrapper } from './components/SearchPageHeaderMetaWrapper/SearchPageHeaderMetaWrapper';
import { SearchPageSeoTitleAndCopy } from './components/SearchPageSeoContent/SearchPageSeoTitleAndCopy';
import { SearchPageTabs } from './components/SearchPageTabs/SearchPageTabs';
import { SearchPageZeroResults } from './components/SearchPageZeroResults/SearchPageZeroResults';
import { SearchResultsBreadcrumbs } from './components/SearchResultsBreadcrumbs/SearchResultsBreadcrumbs';
import { SeeWhatsPopularSection } from './components/SeeWhatsPopularSection/SeeWhatsPopularSection';
import { useGetAppliedFilterChipValues } from './hooks/useGetAppliedFilterChipValues';
import { useGetSearchPageCanonicalUrl } from './hooks/useGetSearchPageCanonicalUrl';
import { useHandleRefetchOnExcludedHouse } from './hooks/useHandleRefetchOnExcludedHouse';
import { useNavigateSearchPageWithParams } from './hooks/useNavigateSearchPageWithParams';
import { useRedirectToValidPage } from './hooks/useRedirectToValidPage';

const SearchPage = () => {
    const { pathname, search } = useLocation();

    const isCollectionsPage = getIsCollectionsPage(pathname);
    const isOnSearchPage = getIsSearchPage(pathname);
    const isSEOSearchPage = getIsSEOSearchPage(pathname) || getIsSEOSearchPageWithLocation(pathname);
    const queryParams = parseQueryParams(search);

    const pageSize = useAppSelector(getSearchPageSize);
    const seoSearchTermData = useAppSelector(getSEOSearchTermData);
    const dispatch = useAppDispatch();

    // Use optimized selectors to batch Redux calls and reduce re-renders
    const {
        archivedSearch: isArchivedSearchPage,
        currentFacets,
        searchState,
    } = useOptimizedSearchSelectors(queryParams, pathname);
    const {
        error: searchFailed,
        isInitialLoading,
        isZeroResults,
        submitted: isLoadingSearchResults,
        totalFound,
        totalPages,
        totalSoldItems,
    } = searchState;

    useGetAppliedFilterChipValues();

    // Experiment: sear-1021-popularity-score-re-ranking-v2
    usePopularityScoreReRanking();

    // Experiment: sear-1387-ltr-mvp-v1-search-ranking-rerun
    useLearningToRankSearchReRanking();

    const navigateSearchPageWithParams = useNavigateSearchPageWithParams();

    const browseFacetOptionIdsFromSeoPath = useMemo(
        () => resolveSearchSeoPathSubjectToBrowseFacetIds(pathname, currentFacets as BrowseCategoryRawFacet[]),
        [pathname, currentFacets]
    );

    const searchQuery = useMemo(() => {
        return buildSearchQuery({
            browseFacetOptionIds: browseFacetOptionIdsFromSeoPath,
            pathname,
            queryParams,
            searchPageSize: pageSize,
            similarItems: true,
        });
    }, [browseFacetOptionIdsFromSeoPath, pathname, queryParams, pageSize]);
    const { citySlug, distance, page, ranges, region, searchTerm } = searchQuery;

    const displaySearchTerm = useMemo(() => {
        return seoSearchTermData?.term || cleanSearchTerms(searchTerm);
    }, [seoSearchTermData?.term, searchTerm]);

    const displayLocation = useMemo(() => {
        const locationString = formatLocationString(citySlug, region, getStateName);
        return locationString ? ` ${locationString}` : '';
    }, [citySlug, region]);

    // if this a SEO page, we need to add the keyword to the save search params from the slug.
    const saveSearchParams = getSaveSearchParams({ ...queryParams, keyword: searchTerm });

    useHandleRefetchOnExcludedHouse({ searchQuery });

    const shouldShowRelatedSearchesSuggestions = Boolean(searchTerm);

    // Redirect to page 1 if requested page exceeds totalPages (e.g. /search/ping/?page=1000 but totalPages is 4)
    useRedirectToValidPage({
        isInitialLoading,
        isLoadingSearchResults,
        page,
        queryParams,
        totalPages,
    });

    const changePageAndNavigate = (selected: number) => {
        const page = selected + 1;
        const newParams = modifyParamsWithPage(queryParams, page);
        navigateSearchPageWithParams({
            checkForPageRemoval: true,
            filterAction: FilterAction.PageChanged,
            params: newParams,
        });
    };

    const changePageSizeAndNavigate = (pageSize: number) => {
        dispatch(changePaginationPageSize(pageSize));
        changePaginationPageSize(pageSize);
        const newParams = modifyParamsWithPageSize(queryParams, pageSize);
        navigateSearchPageWithParams({
            checkForPageRemoval: true,
            filterAction: FilterAction.PageSizeChanged,
            params: newParams,
        });
    };

    const onChange = ({ page, pageSize }: { page: number; pageSize: number }) => {
        if (!pageSize) {
            changePageAndNavigate(page - 1);
        } else {
            changePageSizeAndNavigate(pageSize);
        }
    };

    let canonicalURL = useGetSearchPageCanonicalUrl();

    const { hasNoFilterTermOrRangeResults, hasNoKeywordSearchResults } = useMemo(() => {
        const filterTermQuery = queryParams[paramTypes.FILTER_TERM] || null;
        const filterTerms: string[] = filterTermQuery ? filterTermQuery.split(',') : [];

        const isLoading = isLoadingSearchResults || isInitialLoading;
        const hasNoResults = totalFound === 0 && !isLoading;
        const hasNoFilterTermOrRangeResults =
            (Boolean(filterTerms.length) ||
                Boolean(distance?.zipCode) ||
                !isEmpty(ranges) ||
                Boolean(queryParams.country) ||
                Boolean(queryParams.state) ||
                totalSoldItems > 0) &&
            hasNoResults;

        const hasNoKeywordSearchResults = !hasNoFilterTermOrRangeResults && hasNoResults && totalSoldItems === 0;

        return { hasNoFilterTermOrRangeResults, hasNoKeywordSearchResults };
    }, [distance?.zipCode, isInitialLoading, isLoadingSearchResults, queryParams, ranges, totalFound, totalSoldItems]);

    return (
        <div>
            {isCollectionsPage && (
                <>
                    <div className="m-auto max-w-[1644px] px-md xsMax:px-sm">
                        <SearchResultsBreadcrumbs />
                    </div>
                    <MarketingEventBanner isArchivedSearchPage={isArchivedSearchPage} />
                </>
            )}

            <div
                className={cn('m-auto max-w-[1644px] px-md py-md xsMax:px-sm xsMax:py-sm', {
                    'pt-sm': isCollectionsPage,
                    'pt-xs': isSEOSearchPage,
                })}
            >
                <SearchPageHeaderMetaWrapper
                    canonicalURL={canonicalURL}
                    displayLocation={displayLocation}
                    displaySearchTerm={displaySearchTerm}
                    isCollectionsPage={isCollectionsPage}
                    isSEOSearchPage={isSEOSearchPage}
                    page={page}
                    searchTerm={searchTerm}
                    totalFound={totalFound}
                />

                {isSEOSearchPage && <SearchResultsBreadcrumbs className="pb-sm" />}
                {isOnSearchPage && <CompleteAccountBanner className="mb-lg mt-xs max-w-full xsMax:mb-md xsMax:mt-xs" />}
                {searchFailed ? (
                    <SearchError />
                ) : isZeroResults ? (
                    <SearchPageZeroResults
                        canonicalURL={canonicalURL}
                        distance={distance}
                        hasNoFilterTermOrRangeResults={hasNoFilterTermOrRangeResults}
                        isArchivedSearch={isArchivedSearchPage}
                        isSEOSearchPage={isSEOSearchPage}
                        onChange={onChange}
                        page={page}
                        pageSize={pageSize}
                        saveSearchParams={saveSearchParams}
                        searchTerm={searchTerm}
                        totalFound={totalFound}
                        totalPages={totalPages}
                        totalSoldItems={totalSoldItems}
                    />
                ) : (
                    <>
                        <SearchPageHeader
                            displayLocation={displayLocation}
                            displaySearchTerm={displaySearchTerm}
                            hasNoKeywordSearchResults={hasNoKeywordSearchResults}
                            isArchivedSearch={isArchivedSearchPage}
                            isCollectionsPage={isCollectionsPage}
                            isLoadingSearchResults={isLoadingSearchResults || isInitialLoading}
                            isSEOSearchPage={isSEOSearchPage}
                            saveSearchParams={saveSearchParams}
                            searchTerm={searchTerm}
                            totalFound={totalFound}
                            totalSoldItems={totalSoldItems}
                        />

                        {hasNoKeywordSearchResults ? (
                            // NO RESULTS
                            <SeeWhatsPopularSection />
                        ) : (
                            <>
                                {shouldShowRelatedSearchesSuggestions && <RelatedSearchesSuggestions />}
                                <Pagination
                                    canonicalUrl={canonicalURL}
                                    onChange={onChange}
                                    pageSizeOptions={PAGE_ITEMS_TO_VIEW}
                                    paginationFilter={{ page, pageSize, totalRecords: totalFound }}
                                    totalPages={totalPages}
                                >
                                    <SearchPageTabs
                                        distance={distance}
                                        hasNoFilterTermOrRangeResults={hasNoFilterTermOrRangeResults}
                                        isArchivedSearch={isArchivedSearchPage}
                                        isSEOSearchPage={isSEOSearchPage}
                                        searchTerm={searchTerm}
                                        totalSoldItems={totalSoldItems}
                                    />
                                </Pagination>
                                {isSEOSearchPage && seoSearchTermData && (
                                    <>
                                        <SearchPageSeoTitleAndCopy seoSearchTermData={seoSearchTermData} />
                                        <RelatedSearches
                                            containerClasses="mx-auto mt-lg max-w-[1240px] p-sm smMin:p-lg"
                                            headingMessageId="searchPage.seoPage.relatedSearches"
                                            labelKey="label"
                                            relatedTerms={seoSearchTermData?.relatedTerms || []}
                                            urlKey="url"
                                        />
                                    </>
                                )}
                                <TrackPageBottomReachedTrigger />
                                <SearchResultsAdContainer
                                    page={page}
                                    searchTerm={searchTerm}
                                />
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default SearchPage;
